import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { studentMasterAPI } from '../../services/api';
import AddStudentModal from './AddStudentModal';
import EditStudentModal from './EditStudentModal';
import StudentDetailsModal from './StudentDetailsModal';
import './StudentMaster.css';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import ReportIssueModal from '../StudentUniform/ReportIssueModal';

const StudentMaster = () => {
  const { t } = useTranslation(['studentMaster', 'common']);
  // User info from localStorage
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');

  // View mode: 'hierarchy' or 'table'
  const [viewMode, setViewMode] = useState('hierarchy');

  // Filters
  const [filters, setFilters] = useState({
    ppd: '',
    schoolCode: '',
    enrollYear: null,
    tahun: '',
    kelas: '',
    namePartial: '',
    icPartial: '',
    jantina: '',
    citizenStatus: '',
    page: 1,
    pageSize: 50,
    sortBy: 'Name',
    sortOrder: 'ASC'
  });

  // Data
  const [hierarchyData, setHierarchyData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 50
  });

  // Dropdown data
  const [ppdList, setPpdList] = useState([]);
  const [schoolsList, setSchoolsList] = useState([]);
  const [enrollYearsList, setEnrollYearsList] = useState([]);
  const [tahunsList, setTahunsList] = useState([]);
  const [kelasList, setKelasList] = useState([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Lazy loading hierarchy data - track what's been loaded
  const [loadedPPDs, setLoadedPPDs] = useState([]);
  const [loadedSchools, setLoadedSchools] = useState({}); // { ppdCode: [...schools] }
  const [loadedTahuns, setLoadedTahuns] = useState({});   // { schoolCode: [...tahuns] }
  const [loadedKelas, setLoadedKelas] = useState({});     // { "schoolCode-tahun": [...kelas] }
  const [loadedStudents, setLoadedStudents] = useState({}); // { "schoolCode-tahun-kelas": {...studentData} }

  // Track loading states for each node
  const [loadingNodes, setLoadingNodes] = useState({});

  // Expanded state for accordions
  const [expandedPPDs, setExpandedPPDs] = useState({});
  const [expandedSchools, setExpandedSchools] = useState({});
  const [expandedTahuns, setExpandedTahuns] = useState({});
  const [expandedKelas, setExpandedKelas] = useState({});

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);

  // Report Issue modal states
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [screenshot, setScreenshot] = useState(null);

  // Historical view tracking - when viewing past years, data is read-only
  const [currentEnrollYear, setCurrentEnrollYear] = useState(null);
  const [isHistoricalView, setIsHistoricalView] = useState(false);

  // Context menu for delete with enrollment selection and reasons
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    studentId: null,
    studentName: '',
    enrollments: [],
    showReasons: false,
    selectedEnrollYear: null
  });

  // Load current enrollment year on mount (for reference only, not auto-selected)
  useEffect(() => {
    const loadCurrentYear = async () => {
      try {
        const response = await studentMasterAPI.getCurrentEnrollmentYear();
        if (response.success) {
          const year = response.data;
          setCurrentEnrollYear(year);
          // DO NOT auto-set in filters - user must explicitly select
        }
      } catch (error) {
        console.error('Failed to load current enrollment year:', error);
        // Fallback to current calendar year
        const fallbackYear = new Date().getFullYear();
        setCurrentEnrollYear(fallbackYear);
      }
    };
    loadCurrentYear();
  }, []);

  // Load user info from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setUserRole(parsedUser.role || '');

      // Auto-set school code for Headmaster/Teacher
      if (parsedUser.role === 'HEADMASTER' || parsedUser.role === 'TEACHER') {
        setFilters(prev => ({ ...prev, schoolCode: parsedUser.schoolCode || '' }));
      }

      // Auto-set PPD for PPD users
      if (parsedUser.role === 'PPD') {
        setFilters(prev => ({ ...prev, ppd: parsedUser.ppd || '' }));
      }
    }
  }, []);

  // Load dropdown data
  useEffect(() => {
    loadDropdowns();
  }, [userRole, user]);

  // Load students when filters change
  useEffect(() => {
    if (userRole) {
      loadStudents();
    }
  }, [filters, viewMode, userRole]);

  const loadDropdowns = async () => {
    try {
      // Load PPD list for SuperAdmin
      let ppdPromise = Promise.resolve({ success: true, data: [] });
      if (userRole === 'SUPERADMIN' || userRole === 'ADMIN' || userRole === 'PPD') {
        ppdPromise = studentMasterAPI.getPPDs();
      }

      // Load schools list (filtered by PPD if selected)
      let schoolsPromise = Promise.resolve({ success: true, data: [] });
      if (userRole === 'SUPERADMIN' || userRole === 'ADMIN' || userRole === 'PPD') {
        schoolsPromise = studentMasterAPI.getSchools(filters.ppd || null);
      }

      const [ppds, schools, enrollYears, tahuns, kelas] = await Promise.all([
        ppdPromise,
        schoolsPromise,
        studentMasterAPI.getEnrollmentYears(filters.schoolCode || null),
        studentMasterAPI.getTahuns(filters.schoolCode || null),
        studentMasterAPI.getKelas(filters.schoolCode || null, filters.tahun || null)
      ]);

      if (ppds.success) setPpdList(ppds.data || []);
      if (schools.success) setSchoolsList(schools.data || []);
      if (enrollYears.success) setEnrollYearsList(enrollYears.data || []);
      if (tahuns.success) setTahunsList(tahuns.data || []);
      if (kelas.success) setKelasList(kelas.data || []);
    } catch (error) {
      console.error('Failed to load dropdowns:', error);
    }
  };

  const loadStudents = async () => {
    setIsLoading(true);
    try {
      if (viewMode === 'hierarchy') {
        // SUPERADMIN/ADMIN: Use lazy loading (PPD → Schools → Tahuns → Kelas → Students)
        if (userRole === 'SUPERADMIN' || userRole === 'ADMIN') {
          const response = await studentMasterAPI.getPPDHierarchy({
            enrollYear: filters.enrollYear,
            tahun: filters.tahun
          });
          if (response.success) {
            setLoadedPPDs(response.data || []);
            // Calculate totals from PPD data
            const totalStudents = (response.data || []).reduce((sum, ppd) => sum + (ppd.totalStudents || 0), 0);
            const totalMale = (response.data || []).reduce((sum, ppd) => sum + (ppd.maleCount || 0), 0);
            const totalFemale = (response.data || []).reduce((sum, ppd) => sum + (ppd.femaleCount || 0), 0);
            setHierarchyData({
              totalStudents,
              totalMale,
              totalFemale,
              ppdGroups: response.data || []
            });
          } else {
            Swal.fire('Error', response.message || 'Failed to load hierarchy', 'error');
          }
        } else if (userRole === 'PPD') {
          // PPD: Use lazy loading (Schools → Tahuns → Kelas → Students) scoped to their PPD
          const ppdCode = user?.ppd; // For PPD users, ppd contains the actual PPD code that matches School.PPD
          const response = await studentMasterAPI.getSchoolsForPPD(ppdCode, {
            enrollYear: filters.enrollYear,
            tahun: filters.tahun
          });
          if (response.success) {
            const schools = response.data || [];
            // Store schools under a special key for PPD view
            setLoadedSchools(prev => ({
              ...prev,
              [ppdCode]: schools
            }));
            // Calculate totals from schools data
            const totalStudents = schools.reduce((sum, school) => sum + (school.totalStudents || 0), 0);
            const totalMale = schools.reduce((sum, school) => sum + (school.maleCount || 0), 0);
            const totalFemale = schools.reduce((sum, school) => sum + (school.femaleCount || 0), 0);
            setHierarchyData({
              totalStudents,
              totalMale,
              totalFemale,
              ppdCode,
              schools
            });
          } else {
            Swal.fire('Error', response.message || 'Failed to load schools', 'error');
          }
        } else {
          // HEADMASTER/TEACHER: Use old hierarchy (school-scoped, loads all at once)
          const response = await studentMasterAPI.getStudentHierarchy({
            schoolCode: filters.schoolCode,
            enrollYear: filters.enrollYear,
            tahun: filters.tahun
          });
          if (response.success) {
            setHierarchyData(response.data);
          } else {
            Swal.fire('Error', response.message || 'Failed to load hierarchy', 'error');
          }
        }
      } else {
        const response = await studentMasterAPI.getStudents(filters);
        if (response.success) {
          setTableData(response.data.students || []);
          setPagination({
            totalRecords: response.data.totalRecords,
            totalPages: response.data.totalPages,
            currentPage: response.data.currentPage,
            pageSize: response.data.pageSize
          });
          // Track historical view status from API response
          if (response.data.currentEnrollYear) {
            setCurrentEnrollYear(response.data.currentEnrollYear);
            setIsHistoricalView(response.data.isHistoricalView || false);
          }
        } else {
          Swal.fire('Error', response.message || 'Failed to load students', 'error');
        }
      }
    } catch (error) {
      console.error('Failed to load students:', error);
      Swal.fire('Error', 'Failed to load student data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page on filter change
    }));

    // Update historical view flag when enrollment year changes
    if (field === 'enrollYear') {
      setIsHistoricalView(value < currentEnrollYear);
    }

    // Reload dependent dropdowns
    if (field === 'ppd' || field === 'schoolCode' || field === 'tahun') {
      loadDropdowns();
    }
  };

  const handleClearFilters = () => {
    setFilters({
      ppd: '',
      schoolCode: userRole === 'HEADMASTER' || userRole === 'TEACHER' ? user.schoolCode : '',
      enrollYear: null,
      tahun: '',
      kelas: '',
      namePartial: '',
      icPartial: '',
      jantina: '',
      citizenStatus: '',
      page: 1,
      pageSize: 50,
      sortBy: 'Name',
      sortOrder: 'ASC'
    });
  };

  const handleAddStudent = () => {
    setShowAddModal(true);
  };

  const handleEditStudent = (student) => {
    setSelectedStudent(student);
    setShowEditModal(true);
  };

  // Show context menu for delete action - fetch enrollments first
  const handleDeleteStudent = async (event, studentId, studentName) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      // Fetch active enrollments for this student
      const response = await studentMasterAPI.getStudentEnrollments(studentId);

      if (!response.success || !response.data || response.data.length === 0) {
        Swal.fire('Maklumat', 'Tiada pendaftaran aktif untuk pelajar ini', 'info');
        return;
      }

      // Show context menu with enrollments
      setContextMenu({
        visible: true,
        x: event.pageX,
        y: event.pageY,
        studentId,
        studentName,
        enrollments: response.data,
        showReasons: false,
        selectedEnrollYear: null
      });
    } catch (error) {
      console.error('Failed to fetch enrollments:', error);
      Swal.fire('Ralat', 'Gagal mendapatkan maklumat pendaftaran', 'error');
    }
  };

  // Handle enrollment year selection - show reasons submenu
  const handleEnrollmentSelect = (enrollYear) => {
    setContextMenu(prev => ({
      ...prev,
      showReasons: true,
      selectedEnrollYear: enrollYear
    }));
  };

  // Handle reason selection - execute delete
  const handleReasonSelect = async (reason) => {
    const { studentId, studentName, selectedEnrollYear } = contextMenu;
    setContextMenu({ ...contextMenu, visible: false });

    if (reason === 'OTHER') {
      // Show SweetAlert for custom reason
      const result = await Swal.fire({
        title: 'Nyatakan Sebab',
        input: 'text',
        inputPlaceholder: 'Masukkan sebab...',
        showCancelButton: true,
        confirmButtonText: 'Sahkan',
        cancelButtonText: 'Batal'
      });

      if (result.isConfirmed && result.value) {
        executeDelete(studentId, studentName, selectedEnrollYear, `OTHER: ${result.value}`);
      }
    } else {
      executeDelete(studentId, studentName, selectedEnrollYear, reason);
    }
  };

  // Execute the actual delete operation
  const executeDelete = async (studentId, studentName, enrollYear, reason) => {
    try {
      const response = await studentMasterAPI.deleteStudent(studentId, enrollYear, reason);
      if (response.success) {
        Swal.fire('Berjaya', 'Pendaftaran pelajar berjaya ditandakan sebagai tidak aktif', 'success');
        loadStudents(); // Reload data
      } else {
        Swal.fire('Gagal', response.message || 'Gagal padam pendaftaran pelajar', 'error');
      }
    } catch (error) {
      console.error('Failed to delete student enrollment:', error);
      Swal.fire('Gagal', 'Ralat semasa padam pendaftaran pelajar', 'error');
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClick = () => setContextMenu({ ...contextMenu, visible: false });
    if (contextMenu.visible) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu.visible]);

  // LAZY LOADING: Load schools when PPD is expanded
  const togglePPDExpansion = async (ppdCode) => {
    const isCurrentlyExpanded = expandedPPDs[ppdCode];

    // Toggle expansion state
    setExpandedPPDs(prev => ({
      ...prev,
      [ppdCode]: !prev[ppdCode]
    }));

    // If expanding and schools not loaded yet, load them
    if (!isCurrentlyExpanded && !loadedSchools[ppdCode]) {
      const nodeKey = `ppd-${ppdCode}`;
      setLoadingNodes(prev => ({ ...prev, [nodeKey]: true }));

      try {
        const response = await studentMasterAPI.getSchoolsForPPD(ppdCode, {
          enrollYear: filters.enrollYear,
          tahun: filters.tahun
        });

        if (response.success) {
          setLoadedSchools(prev => ({
            ...prev,
            [ppdCode]: response.data || []
          }));
        } else {
          Swal.fire('Error', `Failed to load schools for ${ppdCode}`, 'error');
        }
      } catch (error) {
        console.error(`Failed to load schools for ${ppdCode}:`, error);
        Swal.fire('Error', 'Failed to load schools', 'error');
      } finally {
        setLoadingNodes(prev => {
          const newState = { ...prev };
          delete newState[nodeKey];
          return newState;
        });
      }
    }
  };

  // LAZY LOADING: Load tahuns when school is expanded
  const toggleSchoolExpansion = async (schoolCode) => {
    const isCurrentlyExpanded = expandedSchools[schoolCode];

    // Toggle expansion state
    setExpandedSchools(prev => ({
      ...prev,
      [schoolCode]: !prev[schoolCode]
    }));

    // If expanding and tahuns not loaded yet, load them
    if (!isCurrentlyExpanded && !loadedTahuns[schoolCode]) {
      const nodeKey = `school-${schoolCode}`;
      setLoadingNodes(prev => ({ ...prev, [nodeKey]: true }));

      try {
        const response = await studentMasterAPI.getTahunsForSchool(schoolCode, {
          enrollYear: filters.enrollYear
        });

        if (response.success) {
          setLoadedTahuns(prev => ({
            ...prev,
            [schoolCode]: response.data || []
          }));
        } else {
          Swal.fire('Error', `Failed to load tahuns for school ${schoolCode}`, 'error');
        }
      } catch (error) {
        console.error(`Failed to load tahuns for school ${schoolCode}:`, error);
        Swal.fire('Error', 'Failed to load tahuns', 'error');
      } finally {
        setLoadingNodes(prev => {
          const newState = { ...prev };
          delete newState[nodeKey];
          return newState;
        });
      }
    }
  };

  // Simple toggle for old hierarchy (non-lazy, data already loaded)
  const toggleSchoolExpansionSimple = (key) => {
    setExpandedSchools(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleTahunExpansionSimple = (key) => {
    setExpandedTahuns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleKelasExpansionSimple = (key) => {
    setExpandedKelas(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // LAZY LOADING: Load kelas when tahun is expanded
  const toggleTahunExpansion = async (schoolCode, tahun) => {
    const key = `${schoolCode}-${tahun}`;
    const isCurrentlyExpanded = expandedTahuns[key];

    // Toggle expansion state
    setExpandedTahuns(prev => ({
      ...prev,
      [key]: !prev[key]
    }));

    // If expanding and kelas not loaded yet, load them
    if (!isCurrentlyExpanded && !loadedKelas[key]) {
      const nodeKey = `tahun-${key}`;
      setLoadingNodes(prev => ({ ...prev, [nodeKey]: true }));

      try {
        const response = await studentMasterAPI.getKelasForSchoolTahun(schoolCode, tahun, {
          enrollYear: filters.enrollYear
        });

        if (response.success) {
          setLoadedKelas(prev => ({
            ...prev,
            [key]: response.data || []
          }));
        } else {
          Swal.fire('Error', `Failed to load kelas for tahun ${tahun}`, 'error');
        }
      } catch (error) {
        console.error(`Failed to load kelas for ${key}:`, error);
        Swal.fire('Error', 'Failed to load kelas', 'error');
      } finally {
        setLoadingNodes(prev => {
          const newState = { ...prev };
          delete newState[nodeKey];
          return newState;
        });
      }
    }
  };

  // LAZY LOADING: Load students when kelas is expanded
  const toggleKelasExpansion = async (schoolCode, tahun, kelas) => {
    const key = `${schoolCode}-${tahun}-${kelas}`;
    const isCurrentlyExpanded = expandedKelas[key];

    // Toggle expansion state
    setExpandedKelas(prev => ({
      ...prev,
      [key]: !prev[key]
    }));

    // If expanding and students not loaded yet, load them
    if (!isCurrentlyExpanded && !loadedStudents[key]) {
      const nodeKey = `kelas-${key}`;
      setLoadingNodes(prev => ({ ...prev, [nodeKey]: true }));

      try {
        const response = await studentMasterAPI.getStudentsForKelas(schoolCode, tahun, kelas, {
          enrollYear: filters.enrollYear
        });

        if (response.success) {
          setLoadedStudents(prev => ({
            ...prev,
            [key]: response.data || {}
          }));
        } else {
          Swal.fire('Error', `Failed to load students for kelas ${kelas}`, 'error');
        }
      } catch (error) {
        console.error(`Failed to load students for ${key}:`, error);
        Swal.fire('Error', 'Failed to load students', 'error');
      } finally {
        setLoadingNodes(prev => {
          const newState = { ...prev };
          delete newState[nodeKey];
          return newState;
        });
      }
    }
  };

  // Format IC for display with dashes (XXXXXX-XX-XXXX)
  // Backend already handles masking based on permissions
  const formatIC = (ic) => {
    if (!ic) return '-';
    if (ic.length < 4) return ic; // Already masked (last 4 digits only)
    if (ic.length === 12) {
      // Format 12-digit IC: XXXXXX-XX-XXXX
      return `${ic.substring(0, 6)}-${ic.substring(6, 8)}-${ic.substring(8)}`;
    }
    // For any other format, return as-is
    return ic;
  };

  const handleViewDetails = (studentId) => {
    setSelectedStudentId(studentId);
    setShowDetailsModal(true);
  };

  const renderHierarchyView = () => {
    if (!hierarchyData) return <div className="no-data">Tiada data</div>;

    // Headmaster: Direct Tahun → Kelas → Students (data already loaded)
    if (userRole === 'HEADMASTER' || userRole === 'TEACHER') {
      return (
        <div className="hierarchy-container">
          {hierarchyData.tahunGroups?.map(tahunGroup => (
            <div key={tahunGroup.tahun} className="tahun-group">
              <div
                className="tahun-header"
                onClick={() => toggleTahunExpansionSimple(tahunGroup.tahun)}
              >
                <span className="expand-icon">{expandedTahuns[tahunGroup.tahun] ? '▼' : '▶'}</span>
                <strong>Tahun {tahunGroup.tahun}</strong>
                <span className="count-badge">{tahunGroup.totalStudents} pelajar</span>
              </div>

              {expandedTahuns[tahunGroup.tahun] && (
                <div className="kelas-list">
                  {tahunGroup.kelas?.map(kelasGroup => (
                    <div key={kelasGroup.kelas} className="kelas-group">
                      <div
                        className="kelas-header"
                        onClick={() => toggleKelasExpansionSimple(`${tahunGroup.tahun}-${kelasGroup.kelas}`)}
                      >
                        <span className="expand-icon">
                          {expandedKelas[`${tahunGroup.tahun}-${kelasGroup.kelas}`] ? '▼' : '▶'}
                        </span>
                        <strong>Kelas {kelasGroup.kelas}</strong>
                        <span className="count-badge">{kelasGroup.totalStudents} pelajar</span>
                        <span className="gender-stats">
                          (L: {kelasGroup.maleCount}, P: {kelasGroup.femaleCount})
                        </span>
                      </div>

                      {expandedKelas[`${tahunGroup.tahun}-${kelasGroup.kelas}`] && (
                        <div className="students-table">
                          {renderStudentsTable(kelasGroup.students)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }

    // PPD: Schools → Tahuns → Kelas → Students (LAZY LOADED, scoped to PPD)
    if (userRole === 'PPD' && hierarchyData?.schools) {
      const schools = hierarchyData.schools;
      return (
        <div className="hierarchy-container">
          {schools.map(schoolGroup => {
            const isSchoolExpanded = expandedSchools[schoolGroup.schoolCode];
            const isSchoolLoading = loadingNodes[`school-${schoolGroup.schoolCode}`];
            const tahuns = loadedTahuns[schoolGroup.schoolCode] || [];

            return (
              <div key={schoolGroup.schoolCode} className="school-group">
                <div
                  className="school-header"
                  onClick={() => toggleSchoolExpansion(schoolGroup.schoolCode)}
                >
                  <span className="expand-icon">
                    {isSchoolExpanded ? '▼' : '▶'}
                  </span>
                  <strong>{schoolGroup.schoolName}</strong>
                  <span className="school-code">({schoolGroup.schoolCode})</span>
                  <span className="count-badge">{schoolGroup.totalStudents} pelajar</span>
                </div>

                {isSchoolExpanded && (
                  <div className="tahun-list">
                    {isSchoolLoading && <div className="loading-node">Memuatkan tahun...</div>}
                    {!isSchoolLoading && tahuns.map(tahunGroup => {
                      const tahunKey = `${schoolGroup.schoolCode}-${tahunGroup.tahun}`;
                      const isTahunExpanded = expandedTahuns[tahunKey];
                      const isTahunLoading = loadingNodes[`tahun-${tahunKey}`];
                      const kelasList = loadedKelas[tahunKey] || [];

                      return (
                        <div key={tahunGroup.tahun} className="tahun-group">
                          <div
                            className="tahun-header"
                            onClick={() => toggleTahunExpansion(schoolGroup.schoolCode, tahunGroup.tahun)}
                          >
                            <span className="expand-icon">
                              {isTahunExpanded ? '▼' : '▶'}
                            </span>
                            <strong>Tahun {tahunGroup.tahun}</strong>
                            <span className="count-badge">{tahunGroup.totalStudents} pelajar</span>
                          </div>

                          {isTahunExpanded && (
                            <div className="kelas-list">
                              {isTahunLoading && <div className="loading-node">Memuatkan kelas...</div>}
                              {!isTahunLoading && kelasList.map(kelasGroup => {
                                const kelasKey = `${schoolGroup.schoolCode}-${tahunGroup.tahun}-${kelasGroup.kelas}`;
                                const isKelasExpanded = expandedKelas[kelasKey];
                                const isKelasLoading = loadingNodes[`kelas-${kelasKey}`];
                                const studentData = loadedStudents[kelasKey];

                                return (
                                  <div key={kelasGroup.kelas} className="kelas-group">
                                    <div
                                      className="kelas-header"
                                      onClick={() => toggleKelasExpansion(schoolGroup.schoolCode, tahunGroup.tahun, kelasGroup.kelas)}
                                    >
                                      <span className="expand-icon">
                                        {isKelasExpanded ? '▼' : '▶'}
                                      </span>
                                      <strong>Kelas {kelasGroup.kelas}</strong>
                                      <span className="count-badge">{kelasGroup.totalStudents} pelajar</span>
                                      <span className="gender-stats">
                                        (L: {kelasGroup.maleCount}, P: {kelasGroup.femaleCount})
                                      </span>
                                    </div>

                                    {isKelasExpanded && (
                                      <div className="students-table">
                                        {isKelasLoading && <div className="loading-node">Memuatkan pelajar...</div>}
                                        {!isKelasLoading && studentData && renderStudentsTable(studentData.students || [])}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // SuperAdmin: PPD → SCHOOL → TAHUN → KELAS → Students (LAZY LOADED)
    if (loadedPPDs && loadedPPDs.length > 0) {
      return (
        <div className="hierarchy-container">
          {loadedPPDs.map(ppdGroup => {
            const ppdCode = ppdGroup.ppd || ppdGroup.PPD;
            const isExpanded = expandedPPDs[ppdCode];
            const isLoading = loadingNodes[`ppd-${ppdCode}`];
            const schools = loadedSchools[ppdCode] || [];

            return (
            <div key={ppdCode} className="ppd-group">
              <div
                className="ppd-header"
                onClick={() => togglePPDExpansion(ppdCode)}
              >
                <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                <strong>{t('studentMaster:hierarchy.ppd')} {ppdCode}</strong>
                <span className="count-badge">{ppdGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
                <span className="gender-stats">
                  {t('studentMaster:hierarchy.genderStats', { male: ppdGroup.maleCount, female: ppdGroup.femaleCount })}
                </span>
              </div>

              {isExpanded && (
                <div className="school-list">
                  {isLoading && <div className="loading-node">{t('studentMaster:loading.schools')}</div>}
                  {!isLoading && schools.map(schoolGroup => {
                    const isSchoolExpanded = expandedSchools[schoolGroup.schoolCode];
                    const isSchoolLoading = loadingNodes[`school-${schoolGroup.schoolCode}`];
                    const tahuns = loadedTahuns[schoolGroup.schoolCode] || [];

                    return (
                    <div key={schoolGroup.schoolCode} className="school-group">
                      <div
                        className="school-header"
                        onClick={() => toggleSchoolExpansion(schoolGroup.schoolCode)}
                      >
                        <span className="expand-icon">
                          {isSchoolExpanded ? '▼' : '▶'}
                        </span>
                        <strong>{schoolGroup.schoolName}</strong>
                        <span className="school-code">({schoolGroup.schoolCode})</span>
                        <span className="count-badge">{schoolGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
                      </div>

                      {isSchoolExpanded && (
                        <div className="tahun-list">
                          {isSchoolLoading && <div className="loading-node">{t('studentMaster:loading.years')}</div>}
                          {!isSchoolLoading && tahuns.map(tahunGroup => {
                            const tahunKey = `${schoolGroup.schoolCode}-${tahunGroup.tahun}`;
                            const isTahunExpanded = expandedTahuns[tahunKey];
                            const isTahunLoading = loadingNodes[`tahun-${tahunKey}`];
                            const kelasList = loadedKelas[tahunKey] || [];

                            return (
                            <div key={tahunGroup.tahun} className="tahun-group">
                              <div
                                className="tahun-header"
                                onClick={() => toggleTahunExpansion(schoolGroup.schoolCode, tahunGroup.tahun)}
                              >
                                <span className="expand-icon">
                                  {isTahunExpanded ? '▼' : '▶'}
                                </span>
                                <strong>{t('studentMaster:hierarchy.tahun')} {tahunGroup.tahun}</strong>
                                <span className="count-badge">{tahunGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
                              </div>

                              {isTahunExpanded && (
                                <div className="kelas-list">
                                  {isTahunLoading && <div className="loading-node">{t('studentMaster:loading.classes')}</div>}
                                  {!isTahunLoading && kelasList.map(kelasGroup => {
                                    const kelasKey = `${schoolGroup.schoolCode}-${tahunGroup.tahun}-${kelasGroup.kelas}`;
                                    const isKelasExpanded = expandedKelas[kelasKey];
                                    const isKelasLoading = loadingNodes[`kelas-${kelasKey}`];
                                    const studentData = loadedStudents[kelasKey];

                                    return (
                                    <div key={kelasGroup.kelas} className="kelas-group">
                                      <div
                                        className="kelas-header"
                                        onClick={() => toggleKelasExpansion(schoolGroup.schoolCode, tahunGroup.tahun, kelasGroup.kelas)}
                                      >
                                        <span className="expand-icon">
                                          {isKelasExpanded ? '▼' : '▶'}
                                        </span>
                                        <strong>{t('studentMaster:hierarchy.kelas')} {kelasGroup.kelas}</strong>
                                        <span className="count-badge">{kelasGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
                                        <span className="gender-stats">
                                          {t('studentMaster:hierarchy.genderStats', { male: kelasGroup.maleCount, female: kelasGroup.femaleCount })}
                                        </span>
                                      </div>

                                      {isKelasExpanded && (
                                        <div className="students-table">
                                          {isKelasLoading && <div className="loading-node">{t('studentMaster:loading.students')}</div>}
                                          {!isKelasLoading && studentData && renderStudentsTable(studentData.students || [])}
                                        </div>
                                      )}
                                    </div>
                                  )
                                  })}
                                </div>
                              )}
                            </div>
                          )
                          })}
                        </div>
                      )}
                    </div>
                  )
                  })}
                </div>
              )}
            </div>
          )
          })}
        </div>
      );
    }

    // HEADMASTER/TEACHER: School → Tahun → Kelas → Students (data already loaded)
    return (
      <div className="hierarchy-container">
        {hierarchyData.schools?.map(schoolGroup => (
          <div key={schoolGroup.schoolCode} className="school-group">
            <div
              className="school-header"
              onClick={() => toggleSchoolExpansionSimple(schoolGroup.schoolCode)}
            >
              <span className="expand-icon">{expandedSchools[schoolGroup.schoolCode] ? '▼' : '▶'}</span>
              <strong>{schoolGroup.schoolName}</strong>
              <span className="school-code">({schoolGroup.schoolCode})</span>
              <span className="count-badge">{schoolGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
            </div>

            {expandedSchools[schoolGroup.schoolCode] && (
              <div className="tahun-list">
                {schoolGroup.tahuns?.map(tahunGroup => (
                  <div key={tahunGroup.tahun} className="tahun-group">
                    <div
                      className="tahun-header"
                      onClick={() => toggleTahunExpansionSimple(`${schoolGroup.schoolCode}-${tahunGroup.tahun}`)}
                    >
                      <span className="expand-icon">
                        {expandedTahuns[`${schoolGroup.schoolCode}-${tahunGroup.tahun}`] ? '▼' : '▶'}
                      </span>
                      <strong>{t('studentMaster:hierarchy.tahun')} {tahunGroup.tahun}</strong>
                      <span className="count-badge">{tahunGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
                    </div>

                    {expandedTahuns[`${schoolGroup.schoolCode}-${tahunGroup.tahun}`] && (
                      <div className="kelas-list">
                        {tahunGroup.kelas?.map(kelasGroup => (
                          <div key={kelasGroup.kelas} className="kelas-group">
                            <div
                              className="kelas-header"
                              onClick={() => toggleKelasExpansionSimple(
                                `${schoolGroup.schoolCode}-${tahunGroup.tahun}-${kelasGroup.kelas}`
                              )}
                            >
                              <span className="expand-icon">
                                {expandedKelas[`${schoolGroup.schoolCode}-${tahunGroup.tahun}-${kelasGroup.kelas}`]
                                  ? '▼' : '▶'}
                              </span>
                              <strong>{t('studentMaster:hierarchy.kelas')} {kelasGroup.kelas}</strong>
                              <span className="count-badge">{kelasGroup.totalStudents} {t('studentMaster:hierarchy.students')}</span>
                              <span className="gender-stats">
                                {t('studentMaster:hierarchy.genderStats', { male: kelasGroup.maleCount, female: kelasGroup.femaleCount })}
                              </span>
                            </div>

                            {expandedKelas[`${schoolGroup.schoolCode}-${tahunGroup.tahun}-${kelasGroup.kelas}`] && (
                              <div className="students-table">
                                {renderStudentsTable(kelasGroup.students)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStudentsTable = (students) => {
    if (!students || students.length === 0) {
      return <div className="no-students">{t('studentMaster:messages.noStudents')}</div>;
    }

    return (
      <table className="students-list">
        <thead>
          <tr>
            <th title={t('studentMaster:table.columns.no', { lng: 'en' })}>{t('studentMaster:table.columns.no')}</th>
            <th title={t('studentMaster:table.columns.name', { lng: 'en' })}>{t('studentMaster:table.columns.name')}</th>
            <th title={t('studentMaster:table.columns.icNo', { lng: 'en' })}>{t('studentMaster:table.columns.icNo')}</th>
            <th title={t('studentMaster:table.columns.dob', { lng: 'en' })}>{t('studentMaster:table.columns.dob')}</th>
            <th title={t('studentMaster:table.columns.gender', { lng: 'en' })}>{t('studentMaster:table.columns.gender')}</th>
            <th title={t('studentMaster:table.columns.citizenStatus', { lng: 'en' })}>{t('studentMaster:table.columns.citizenStatus')}</th>
            <th title={t('studentMaster:table.columns.actions', { lng: 'en' })}>{t('studentMaster:table.columns.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {students.map((student, index) => (
            <tr key={student.id}>
              <td>{index + 1}</td>
              <td>{student.name}</td>
              <td>{formatIC(student.ic)}</td>
              <td>{student.dob ? new Date(student.dob).toLocaleDateString('ms-MY') : '-'}</td>
              <td>{student.jantina === 'L' ? t('studentMaster:genderOptions.male') : student.jantina === 'P' ? t('studentMaster:genderOptions.female') : '-'}</td>
              <td>{student.citizenStatus || '-'}</td>
              <td>
                <button
                  className="btn-view"
                  onClick={() => handleViewDetails(student.id)}
                  title={t('studentMaster:tooltips.viewDetails', { lng: 'en' })}
                >
                  👁️
                </button>
                <button
                  className={`btn-edit ${isHistoricalView ? 'disabled' : ''}`}
                  onClick={() => !isHistoricalView && handleEditStudent(student)}
                  disabled={isHistoricalView}
                  title={isHistoricalView
                    ? t('studentMaster:messages.historicalViewReadOnly', { defaultValue: 'Cannot edit - viewing historical data' })
                    : t('studentMaster:tooltips.edit', { lng: 'en' })
                  }
                >
                  ✏️
                </button>
                {userRole === 'SUPERADMIN' && (
                  <button
                    className={`btn-delete ${isHistoricalView ? 'disabled' : ''}`}
                    onClick={(e) => !isHistoricalView && handleDeleteStudent(e, student.id, student.name)}
                    disabled={isHistoricalView}
                    title={isHistoricalView
                      ? t('studentMaster:messages.historicalViewReadOnly', { defaultValue: 'Cannot delete - viewing historical data' })
                      : t('studentMaster:tooltips.delete', { lng: 'en' })
                    }
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderTableView = () => {
    if (tableData.length === 0) {
      return <div className="no-data">{t('studentMaster:messages.noData')}</div>;
    }

    return (
      <div className="table-container">
        <table className="students-table-full">
          <thead>
            <tr>
              <th title={t('studentMaster:table.columns.no', { lng: 'en' })}>{t('studentMaster:table.columns.no')}</th>
              <th title={t('studentMaster:table.columns.name', { lng: 'en' })}>{t('studentMaster:table.columns.name')}</th>
              <th title={t('studentMaster:table.columns.icNo', { lng: 'en' })}>{t('studentMaster:table.columns.icNo')}</th>
              <th title={t('studentMaster:table.columns.dob', { lng: 'en' })}>{t('studentMaster:table.columns.dob')}</th>
              <th title={t('studentMaster:table.columns.gender', { lng: 'en' })}>{t('studentMaster:table.columns.gender')}</th>
              <th title={t('studentMaster:table.columns.school', { lng: 'en' })}>{t('studentMaster:table.columns.school')}</th>
              <th title={t('studentMaster:table.columns.tahun', { lng: 'en' })}>{t('studentMaster:table.columns.tahun')}</th>
              <th title={t('studentMaster:table.columns.kelas', { lng: 'en' })}>{t('studentMaster:table.columns.kelas')}</th>
              <th title={t('studentMaster:table.columns.citizenStatus', { lng: 'en' })}>{t('studentMaster:table.columns.citizenStatus')}</th>
              <th title={t('studentMaster:table.columns.actions', { lng: 'en' })}>{t('studentMaster:table.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((student, index) => (
              <tr key={student.id}>
                <td>{(pagination.currentPage - 1) * pagination.pageSize + index + 1}</td>
                <td>{student.name}</td>
                <td>{formatIC(student.ic)}</td>
                <td>{student.dob ? new Date(student.dob).toLocaleDateString('ms-MY') : '-'}</td>
                <td>{student.jantina === 'L' ? t('studentMaster:genderOptions.male') : student.jantina === 'P' ? t('studentMaster:genderOptions.female') : '-'}</td>
                <td>{student.schoolName || '-'}</td>
                <td>{student.tahun || '-'}</td>
                <td>{student.kelas || '-'}</td>
                <td>{student.citizenStatus || '-'}</td>
                <td>
                  <button
                    className="btn-view"
                    onClick={() => handleViewDetails(student.id)}
                    title={t('studentMaster:tooltips.viewDetails', { lng: 'en' })}
                  >
                    👁️
                  </button>
                  <button
                    className={`btn-edit ${isHistoricalView ? 'disabled' : ''}`}
                    onClick={() => !isHistoricalView && handleEditStudent(student)}
                    disabled={isHistoricalView}
                    title={isHistoricalView
                      ? t('studentMaster:messages.historicalViewReadOnly', { defaultValue: 'Cannot edit - viewing historical data' })
                      : t('studentMaster:tooltips.edit', { lng: 'en' })
                    }
                  >
                    ✏️
                  </button>
                  {userRole === 'SUPERADMIN' && (
                    <button
                      className={`btn-delete ${isHistoricalView ? 'disabled' : ''}`}
                      onClick={(e) => !isHistoricalView && handleDeleteStudent(e, student.id, student.name)}
                      disabled={isHistoricalView}
                      title={isHistoricalView
                        ? t('studentMaster:messages.historicalViewReadOnly', { defaultValue: 'Cannot delete - viewing historical data' })
                        : t('studentMaster:tooltips.delete', { lng: 'en' })
                      }
                    >
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination">
          <button
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={filters.page === 1}
            title={t('studentMaster:pagination.previous', { lng: 'en' })}
          >
            {t('studentMaster:pagination.previous')}
          </button>
          <span>{t('studentMaster:pagination.page', { current: pagination.currentPage, total: pagination.totalPages })}</span>
          <span>{t('studentMaster:pagination.records', { count: pagination.totalRecords })}</span>
          <button
            onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={filters.page >= pagination.totalPages}
            title={t('studentMaster:pagination.next', { lng: 'en' })}
          >
            {t('studentMaster:pagination.next')}
          </button>
        </div>
      </div>
    );
  };

  // Handle Report Issue button click
  const handleReportIssueClick = async () => {
    try {
      Swal.fire({
        title: t('studentMaster:screenshot.capturing'),
        html: t('studentMaster:screenshot.wait'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        width: window.innerWidth,
        height: window.innerHeight
      });

      const imageData = canvas.toDataURL('image/png');
      setScreenshot(imageData);
      setShowReportIssueModal(true);
      Swal.close();
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      Swal.fire({
        icon: 'error',
        title: t('studentMaster:screenshot.captureFailed'),
        text: t('studentMaster:screenshot.captureError')
      });
    }
  };

  // Export to Excel handler
  const [isExporting, setIsExporting] = useState(false);

  const handleExportExcel = async () => {
    if (!filters.ppd) return;

    const result = await Swal.fire({
      icon: 'warning',
      title: t('studentMaster:export.confirmTitle', { defaultValue: 'Peringatan Kerahsiaan Data' }),
      html: t('studentMaster:export.confirmMessage', {
        defaultValue: `<div style="text-align: left; font-size: 14px; line-height: 1.8;">
          <p>Anda akan mengeksport maklumat <strong>SULIT</strong> pelajar termasuk Nombor Kad Pengenalan, nama, dan data peribadi lain.</p>
          <p>Dengan meneruskan, anda bersetuju bahawa:</p>
          <ul style="margin: 8px 0;">
            <li>Data ini adalah <strong>SULIT</strong> dan <strong>TERHAD</strong></li>
            <li>Anda <strong>bertanggungjawab sepenuhnya</strong> terhadap keselamatan data yang dieksport</li>
            <li>Data <strong>TIDAK BOLEH</strong> dikongsi, disalin, atau didedahkan kepada pihak yang tidak berkenaan</li>
          </ul>
          <p style="color: #856404; background: #fff3cd; padding: 8px 12px; border-radius: 4px; margin-top: 12px;">
            <strong>Tindakan ini akan direkodkan dalam log audit sistem.</strong>
          </p>
        </div>`
      }),
      showCancelButton: true,
      confirmButtonText: t('studentMaster:export.confirmButton', { defaultValue: 'Saya Faham & Teruskan' }),
      cancelButtonText: t('studentMaster:export.cancelButton', { defaultValue: 'Batal' }),
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      width: '550px',
      customClass: {
        popup: 'export-disclaimer-popup'
      }
    });

    if (!result.isConfirmed) return;

    setIsExporting(true);
    try {
      const exportFilters = {
        ppd: filters.ppd,
        schoolCode: filters.schoolCode || null,
        enrollYear: filters.enrollYear || null,
        tahun: filters.tahun || null,
        kelas: filters.kelas || null,
        jantina: filters.jantina || null,
        citizenStatus: filters.citizenStatus || null,
        namePartial: filters.namePartial || null,
        icPartial: filters.icPartial || null
      };

      const blob = await studentMasterAPI.exportStudents(exportFilters);
      const filename = `StudentMaster_${filters.ppd}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(blob, filename);

      Swal.fire({
        icon: 'success',
        title: t('studentMaster:export.success', { defaultValue: 'Export Successful' }),
        text: t('studentMaster:export.downloaded', { defaultValue: 'Excel file has been downloaded.' }),
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Export failed:', error);
      Swal.fire('Error', t('studentMaster:export.failed', { defaultValue: 'Failed to export. Please try again.' }), 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="student-master-container">
      {/* Header with Logo and Title */}
      <div className="header-container">
        <img className="logo" src="/KTC.png" alt="KTC" />
        <h2 title={t('studentMaster:title', { lng: 'en' })}>{t('studentMaster:title')}</h2>
        <button className="header-btn btn-report-issue" onClick={handleReportIssueClick} title={t('studentMaster:buttons.reportIssue', { lng: 'en' })}>
          <i className="fas fa-bug"></i>
        </button>
      </div>

      {/* Filters Panel */}
      <div className="filters-panel">
        <h3 title={t('studentMaster:filters.title', { lng: 'en' })}>{t('studentMaster:filters.title')}</h3>
        <div className="filters-grid">
          {(userRole === 'SUPERADMIN' || userRole === 'ADMIN' || userRole === 'PPD') && (
            <>
              <div className="filter-group">
                <label title={t('studentMaster:filters.ppd', { lng: 'en' })}>{t('studentMaster:filters.ppd')}</label>
                <select
                  value={filters.ppd}
                  onChange={(e) => handleFilterChange('ppd', e.target.value)}
                  disabled={userRole === 'PPD'}
                >
                  <option value="">{t('studentMaster:filters.all')}</option>
                  {ppdList.map(ppd => (
                    <option key={ppd} value={ppd}>{ppd}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label title={t('studentMaster:filters.school', { lng: 'en' })}>{t('studentMaster:filters.school')}</label>
                <select
                  value={filters.schoolCode}
                  onChange={(e) => handleFilterChange('schoolCode', e.target.value)}
                >
                  <option value="">{t('studentMaster:filters.all')}</option>
                  {schoolsList.map(school => (
                    <option key={school.schoolCode} value={school.schoolCode}>
                      {school.schoolName} ({school.schoolCode})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="filter-group">
            <label title={t('studentMaster:filters.enrollYear', { lng: 'en' })}>
              {t('studentMaster:filters.enrollYear')} *
            </label>
            <select
              value={filters.enrollYear || ''}
              onChange={(e) => handleFilterChange('enrollYear', e.target.value ? parseInt(e.target.value) : null)}
              style={{
                fontWeight: 'bold',
                backgroundColor: !filters.enrollYear ? '#f8f9fa' : (isHistoricalView ? '#fff3cd' : '#d4edda'),
                border: !filters.enrollYear ? '2px solid #6c757d' : (isHistoricalView ? '2px solid #ffc107' : '2px solid #28a745')
              }}
            >
              <option value="">{t('studentMaster:filters.selectEnrollYear', 'Select enrollment year...')}</option>
              {enrollYearsList.map(year => (
                <option key={year} value={year}>
                  {year} {year === currentEnrollYear ? '(Current)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label title={t('studentMaster:filters.tahun', { lng: 'en' })}>{t('studentMaster:filters.tahun')}</label>
            <select
              value={filters.tahun}
              onChange={(e) => handleFilterChange('tahun', e.target.value)}
            >
              <option value="">{t('studentMaster:filters.all')}</option>
              {tahunsList.map(tahun => (
                <option key={tahun} value={tahun}>{tahun}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label title={t('studentMaster:filters.kelas', { lng: 'en' })}>{t('studentMaster:filters.kelas')}</label>
            <select
              value={filters.kelas}
              onChange={(e) => handleFilterChange('kelas', e.target.value)}
            >
              <option value="">{t('studentMaster:filters.all')}</option>
              {kelasList.map(kelas => (
                <option key={kelas} value={kelas}>{kelas}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label title={t('studentMaster:filters.studentName', { lng: 'en' })}>{t('studentMaster:filters.studentName')}</label>
            <input
              type="text"
              value={filters.namePartial}
              onChange={(e) => handleFilterChange('namePartial', e.target.value)}
              placeholder={t('studentMaster:filters.studentNamePlaceholder')}
            />
          </div>

          <div className="filter-group">
            <label title={t('studentMaster:filters.icNo', { lng: 'en' })}>{t('studentMaster:filters.icNo')}</label>
            <input
              type="text"
              value={filters.icPartial}
              onChange={(e) => handleFilterChange('icPartial', e.target.value)}
              placeholder={t('studentMaster:filters.icNoPlaceholder')}
              maxLength={4}
            />
          </div>

          <div className="filter-group">
            <label title={t('studentMaster:filters.gender', { lng: 'en' })}>{t('studentMaster:filters.gender')}</label>
            <select
              value={filters.jantina}
              onChange={(e) => handleFilterChange('jantina', e.target.value)}
            >
              <option value="">{t('studentMaster:filters.all')}</option>
              <option value="L">{t('studentMaster:genderOptions.male')}</option>
              <option value="P">{t('studentMaster:genderOptions.female')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label title={t('studentMaster:filters.citizenStatus', { lng: 'en' })}>{t('studentMaster:filters.citizenStatus')}</label>
            <select
              value={filters.citizenStatus}
              onChange={(e) => handleFilterChange('citizenStatus', e.target.value)}
            >
              <option value="">{t('studentMaster:filters.all')}</option>
              <option value="WARGANEGARA">{t('studentMaster:citizenStatusOptions.citizen')}</option>
              <option value="PEMASTAUTIN TETAP">{t('studentMaster:citizenStatusOptions.permanentResident')}</option>
              <option value="BUKAN WARGANEGARA">{t('studentMaster:citizenStatusOptions.nonCitizen')}</option>
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn-clear" onClick={handleClearFilters} title={t('studentMaster:filters.clearFilters', { lng: 'en' })}>
            {t('studentMaster:filters.clearFilters')}
          </button>
        </div>
      </div>

      {/* View Mode Toggle and Action Buttons */}
      <div className="view-mode-toggle">
        <button
          className={viewMode === 'hierarchy' ? 'active' : ''}
          onClick={() => setViewMode('hierarchy')}
          title={t('studentMaster:viewMode.hierarchy', { lng: 'en' })}
        >
          {t('studentMaster:viewMode.hierarchy')}
        </button>
        <button
          className={viewMode === 'table' ? 'active' : ''}
          onClick={() => setViewMode('table')}
          title={t('studentMaster:viewMode.table', { lng: 'en' })}
        >
          {t('studentMaster:viewMode.table')}
        </button>
        <div className="action-buttons-right">
          <button
            className={`btn-add ${!filters.ppd || isExporting || (userRole !== 'ADMIN' && userRole !== 'SUPERADMIN') ? 'disabled' : ''}`}
            onClick={handleExportExcel}
            disabled={!filters.ppd || isExporting || (userRole !== 'ADMIN' && userRole !== 'SUPERADMIN')}
            title={userRole !== 'ADMIN' && userRole !== 'SUPERADMIN'
              ? t('studentMaster:export.noPermission', { defaultValue: 'You do not have permission to export' })
              : !filters.ppd
                ? t('studentMaster:export.selectPPD', { defaultValue: 'Select a PPD to enable export' })
                : t('studentMaster:export.exportExcel', { defaultValue: 'Export to Excel' })
            }
          >
            {isExporting
              ? t('studentMaster:export.exporting', { defaultValue: 'Exporting...' })
              : t('studentMaster:export.exportExcel', { defaultValue: 'Export Excel' })
            }
          </button>
          {(userRole === 'HEADMASTER' || userRole === 'SUPERADMIN' || userRole === 'ADMIN') && (
            <button
              className={`btn-add ${isHistoricalView ? 'disabled' : ''}`}
              onClick={handleAddStudent}
              disabled={isHistoricalView}
              title={isHistoricalView
                ? t('studentMaster:messages.historicalViewReadOnly', { defaultValue: 'Cannot add students - viewing historical data' })
                : t('studentMaster:buttons.addStudent', { lng: 'en' })
              }
            >
              {t('studentMaster:buttons.addStudent')}
            </button>
          )}
        </div>
      </div>

      {/* Historical View Warning Banner */}
      {isHistoricalView && (
        <div className="historical-view-banner">
          <i className="fas fa-history"></i>
          <span>
            {t('studentMaster:messages.historicalViewBanner', {
              defaultValue: `Viewing historical data (${filters.enrollYear}). Current year is ${currentEnrollYear}. This data is read-only.`,
              year: filters.enrollYear,
              currentYear: currentEnrollYear
            })}
          </span>
        </div>
      )}

      {/* Statistics */}
      {hierarchyData && viewMode === 'hierarchy' && (
        <div className="statistics-panel">
          <div className="stat-item">
            <span className="stat-label" title={t('studentMaster:statistics.totalStudents', { lng: 'en' })}>{t('studentMaster:statistics.totalStudents')}</span>
            <span className="stat-value">{hierarchyData.totalStudents || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label" title={t('studentMaster:statistics.male', { lng: 'en' })}>{t('studentMaster:statistics.male')}</span>
            <span className="stat-value">{hierarchyData.totalMale || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label" title={t('studentMaster:statistics.female', { lng: 'en' })}>{t('studentMaster:statistics.female')}</span>
            <span className="stat-value">{hierarchyData.totalFemale || 0}</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="content-section">
        {isLoading ? (
          <div className="loading">{t('studentMaster:loading.data')}</div>
        ) : !filters.enrollYear ? (
          <div className="no-data" style={{ textAlign: 'center', padding: '40px', fontSize: '16px', color: '#6c757d' }}>
            <i className="fas fa-info-circle" style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }}></i>
            {t('studentMaster:messages.selectEnrollYear', 'Please select an enrollment year to view student data')}
          </div>
        ) : viewMode === 'hierarchy' ? (
          renderHierarchyView()
        ) : (
          renderTableView()
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddStudentModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadStudents();
          }}
          userSchoolCode={user?.schoolCode}
        />
      )}

      {showEditModal && selectedStudent && (
        <EditStudentModal
          student={selectedStudent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedStudent(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedStudent(null);
            loadStudents();
          }}
        />
      )}

      {showDetailsModal && selectedStudentId && (
        <StudentDetailsModal
          studentId={selectedStudentId}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedStudentId(null);
          }}
        />
      )}

      {/* Context Menu for Delete with Enrollment Selection and Reasons */}
      {contextMenu.visible && (
        <div
          className="removal-context-menu active"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`
          }}
        >
          {!contextMenu.showReasons ? (
            // Show enrollment selection menu
            <>
              <div className="removal-menu-header">Pilih Tahun Pendaftaran</div>
              {contextMenu.enrollments.map((enrollment) => (
                <div
                  key={enrollment.enrollmentId}
                  className="removal-menu-item"
                  onClick={() => handleEnrollmentSelect(enrollment.enrollYear)}
                >
                  <i className="fas fa-calendar-alt"></i>
                  <span>
                    {enrollment.enrollYear} - {enrollment.schoolName}
                    {enrollment.tahun && ` (${enrollment.tahun})`}
                    {enrollment.class && ` - ${enrollment.class}`}
                  </span>
                  <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '12px' }}></i>
                </div>
              ))}
            </>
          ) : (
            // Show removal reasons menu
            <>
              <div className="removal-menu-header">
                <i
                  className="fas fa-arrow-left"
                  style={{ cursor: 'pointer', marginRight: '8px' }}
                  onClick={() => setContextMenu(prev => ({ ...prev, showReasons: false, selectedEnrollYear: null }))}
                ></i>
                Sebab Padam
              </div>
              <div className="removal-menu-item" onClick={() => handleReasonSelect('LEFT_SCHOOL')}>
                <i className="fas fa-door-open"></i>
                <span>Pelajar Keluar Sekolah</span>
              </div>
              <div className="removal-menu-item" onClick={() => handleReasonSelect('GRADUATED')}>
                <i className="fas fa-graduation-cap"></i>
                <span>Tamat Persekolahan</span>
              </div>
              <div className="removal-menu-item" onClick={() => handleReasonSelect('TRANSFERRED')}>
                <i className="fas fa-exchange-alt"></i>
                <span>Pindah Sekolah</span>
              </div>
              <div className="removal-menu-item" onClick={() => handleReasonSelect('WRONG_CLASS')}>
                <i className="fas fa-exclamation-triangle"></i>
                <span>Salah Kelas</span>
              </div>
              <div className="removal-menu-item" onClick={() => handleReasonSelect('DUPLICATE')}>
                <i className="fas fa-copy"></i>
                <span>Rekod Pendua</span>
              </div>
              <div className="removal-menu-item" onClick={() => handleReasonSelect('OTHER')}>
                <i className="fas fa-ellipsis-h"></i>
                <span>Lain-lain...</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <ReportIssueModal
          show={showReportIssueModal}
          onClose={() => setShowReportIssueModal(false)}
          screenshot={screenshot}
          context={{
            screen: t('studentMaster:context.screenName'),
            selectedTahun: filters.tahun,
            selectedKelas: filters.kelas,
            enrollYear: filters.enrollYear,
            filters,
            viewMode,
            totalStudents: viewMode === 'hierarchy' ? hierarchyData?.totalStudents : pagination?.totalRecords,
            userRole
          }}
        />
      )}
    </div>
  );
};

export default StudentMaster;
